/**
 * MarketUpdatesAnimation Component
 *
 * Simple typewriter animation that types out a summary title followed by market update bullets.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { css, keyframes } from '@emotion/react';
import { colors, fontStyles, spacing } from '../../../../styles';

interface MarketUpdatesAnimationProps {
  isActive: boolean;
}

// Cursor blink animation
const blinkKeyframes = keyframes`
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
`;

const BULLETS = [
  { emoji: '🟢', text: 'NVDA +8% on strong AI chip demand' },
  { emoji: '🔴', text: 'Fed holds rates, markets dip 0.5%' },
  { emoji: '⚪', text: 'AAPL announces Q2 earnings date' },
  { emoji: '🟢', text: 'Your portfolio up 2.3% today' },
];

export const MarketUpdatesAnimation: React.FC<MarketUpdatesAnimationProps> = ({ isActive }) => {
  const [currentItem, setCurrentItem] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [completedItems, setCompletedItems] = useState<number[]>([]);
  const typingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextItemRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAllTimeouts = useCallback(() => {
    if (typingRef.current) clearTimeout(typingRef.current);
    if (nextItemRef.current) clearTimeout(nextItemRef.current);
  }, []);

  // Reset when becoming active/inactive
  useEffect(() => {
    if (!isActive) {
      clearAllTimeouts();
      setCurrentItem(0);
      setDisplayText('');
      setCompletedItems([]);
      return;
    }

    setCurrentItem(0);
    setDisplayText('');
    setCompletedItems([]);
  }, [isActive, clearAllTimeouts]);

  // Typing effect
  useEffect(() => {
    if (!isActive || currentItem > BULLETS.length - 1) return;

    const text = BULLETS[currentItem].text;
    let charIndex = 0;
    const charDelay = 300 / text.length;

    const typeNextChar = () => {
      if (charIndex < text.length) {
        setDisplayText(text.slice(0, charIndex + 1));
        charIndex++;
        typingRef.current = setTimeout(typeNextChar, charDelay);
      } else {
        // Typing complete - mark as completed and move to next
        setCompletedItems((prev) => [...prev, currentItem]);
        if (currentItem < BULLETS.length - 1) {
          nextItemRef.current = setTimeout(() => {
            setCurrentItem((prev) => prev + 1);
            setDisplayText('');
          }, 200);
        }
      }
    };

    typeNextChar();

    return () => {
      if (typingRef.current) clearTimeout(typingRef.current);
    };
  }, [isActive, currentItem]);

  // Cleanup on unmount
  useEffect(() => {
    return clearAllTimeouts;
  }, [clearAllTimeouts]);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${spacing.centi};
        padding: ${spacing.deci};
        height: 100%;
        overflow: hidden;
      `}
    >
      {/* Bullets */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${spacing.centi};
          flex: 1;
        `}
      >
        {BULLETS.map((bullet, index) => {
          const isCurrentBullet = index === currentItem;
          const isCompleted = completedItems.includes(index);
          const isVisible = isCompleted || isCurrentBullet;

          if (!isVisible) return null;

          return (
            <div
              key={index}
              css={css`
                display: flex;
                align-items: flex-start;
                gap: ${spacing.milli};
                ${fontStyles.cat};
                color: ${colors.liquorice};
                min-height: 20px;

                html[data-theme='dark'] & {
                  color: ${colors.sugar};
                }
              `}
            >
              <span
                css={css`
                  flex-shrink: 0;
                  font-size: 12px;
                `}
              >
                {bullet.emoji}
              </span>
              <span>
                {isCompleted ? bullet.text : displayText}
                {isCurrentBullet && !isCompleted && (
                  <span
                    css={css`
                      display: inline-block;
                      width: 1px;
                      height: 12px;
                      background: ${colors.petrolBlue};
                      margin-left: 1px;
                      vertical-align: middle;
                      animation: ${blinkKeyframes} 0.5s infinite;

                      html[data-theme='dark'] & {
                        background: ${colors.skyBlue};
                      }
                    `}
                  />
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
